import { addDays, format, parseISO } from 'date-fns';
import { Service } from 'features/quotation/types';
import { FlightAncillaryTypeEnum, TransferTypeEnum, TypeServiceEnum, serviceTypes } from 'features/salesOrder/types';
import { tariffProviderNotAssignedId } from 'pages/SalesOrder/SalesOrderScreen/constants';
import {
	ExtraServiceItem,
	getValidatingCarrierCodeFromQuotation,
	suggestFlightAncillaryType,
} from '../mappers/quotationServiceMappers';
import { LoadedQuotation, WizardSavedPassenger } from '../types';

export const WIZARD_OTHER_SERVICE_TYPES: { value: string; name: string }[] = [
	{ value: 'seguros', name: 'Asistencia en viajes o Seguros' },
	{ value: 'actividades_menos1dia', name: 'Actividades, paseos o excursiones de solo 1 día' },
	{ value: 'pkts_o_exc_mas1dia', name: 'Paquetes o excursiones de más de 1 día' },
	{ value: 'ferry', name: 'Ferry' },
	{ value: 'rent_a_car', name: 'Alquiler de auto' },
	{ value: 'crucero', name: 'Crucero' },
	{ value: 'circuito', name: 'Circuito' },
	{ value: 'evento_o_concierto', name: 'Eventos o Conciertos con entradas' },
	{ value: 'entradas', name: 'Entradas a espectáculos o parques' },
	{ value: 'documentos', name: 'Visas u otros Documentos' },
	{ value: 'otros_servicios', name: 'Otros (detallar en nombre y notas)' },
];

export const WIZARD_EXCLUDED_SERVICE_TYPES: TypeServiceEnum[] = [TypeServiceEnum.Flight, TypeServiceEnum.Accommodation];

export const WIZARD_SALES_ORDER_SERVICE_TYPE_OPTIONS = serviceTypes
	.filter((t) => !WIZARD_EXCLUDED_SERVICE_TYPES.includes(t.id))
	.map((t) => ({ value: t.id, label: t.name }));

export const WIZARD_FLIGHT_ANCILLARY_TYPE_OPTIONS: { value: FlightAncillaryTypeEnum; label: string }[] = [
	{ value: FlightAncillaryTypeEnum.CheckedBaggage, label: 'Equipaje facturado' },
	{ value: FlightAncillaryTypeEnum.CabinBaggage, label: 'Equipaje de cabina' },
	{ value: FlightAncillaryTypeEnum.SpecialLuggage, label: 'Equipaje especial' },
	{ value: FlightAncillaryTypeEnum.Seat, label: 'Asiento' },
	{ value: FlightAncillaryTypeEnum.Pet, label: 'Mascota' },
	{ value: FlightAncillaryTypeEnum.Meal, label: 'Comida' },
];

export type TransferSegmentDraft = {
	transferType: TransferTypeEnum;
	originName: string;
	destinationName: string;
	startDate: string;
	endDate: string;
};

export type ExtraServiceFormDraft = {
	enabled: boolean;
	salesOrderType: TypeServiceEnum;
	otherServiceType: string;
	flightAncillaryType: FlightAncillaryTypeEnum;
	validatingCarrierCode: string;
	feeServiceType: string;
	reference: string;
	tariffProviderId: string;
	sameTariffServiceProvider: boolean;
	serviceProviderId: string;
	cost: number;
	startDate: string;
	endDate: string;
	limitDate: string;
	passengerIds: string[];
	segment: TransferSegmentDraft;
};

export const suggestSalesOrderType = (service: Service): TypeServiceEnum => {
	const name = service.name.toLowerCase();
	if (name.includes('equipaje') || name.includes('maleta') || name.includes('ancillary')) {
		return TypeServiceEnum.FlightAncillary;
	}
	if (name.includes('fee') || name.includes('cargo') || name.includes('comisión')) {
		return TypeServiceEnum.Fee;
	}
	if (
		name.includes('traslado') ||
		name.includes('transfer') ||
		name.includes('llegada') ||
		name.includes('salida') ||
		(name.includes('alojamiento') && name.includes('/'))
	) {
		return TypeServiceEnum.Transfer;
	}
	return TypeServiceEnum.Other;
};

export const parseTransferFromServiceName = (name: string): Omit<TransferSegmentDraft, 'startDate' | 'endDate'> => {
	const legMatch = name.match(/\(([^)]+)\)/i);
	let transferType = TransferTypeEnum.In;
	if (legMatch) {
		const leg = legMatch[1].toLowerCase();
		if (leg.includes('ida y vuelta') || leg.includes('round')) {
			transferType = TransferTypeEnum.InOut;
		}
	}
	const withoutParens = name.replace(/\([^)]+\)/g, '').trim();
	const parts = withoutParens
		.split('/')
		.map((s) =>
			s
				.trim()
				.replace(/\s+o viceversa$/i, '')
				.trim(),
		)
		.filter(Boolean);
	const originName = parts[0] || name;
	const destinationName = parts[1] || originName;

	return { originName, destinationName, transferType };
};

export const computeQuotationServiceTotalCost = (service: Service, passengerCount: number): number => {
	if (service.serviceType === 'perGroup') {
		return service.fareCost;
	}
	if (passengerCount <= 0) {
		return service.fareCost;
	}
	return Number((service.fareCost * passengerCount).toFixed(2));
};

export const normalizeReferenceForApi = (reference: string): string => {
	const trimmed = reference.trim();
	return trimmed.length > 0 ? trimmed.toUpperCase() : '-';
};

export const getDatesForExtraServiceItem = (
	item: ExtraServiceItem,
	quotation: LoadedQuotation,
): { startDate: string; endDate: string; limitDate: string } => {
	if (item.kind === 'destination') {
		const start = item.destination.checking;
		const end = format(addDays(parseISO(start), item.destination.nights), 'yyyy-MM-dd');
		return { startDate: start, endDate: end, limitDate: start };
	}
	const start =
		('destination' in quotation && quotation.destination?.[0]?.checking) || format(new Date(), 'yyyy-MM-dd');
	return { startDate: start, endDate: start, limitDate: start };
};

export const createExtraServiceFormDraft = (
	item: ExtraServiceItem,
	quotation: LoadedQuotation,
	savedPassengers: WizardSavedPassenger[],
): ExtraServiceFormDraft => {
	const { service } = item;
	const dates = getDatesForExtraServiceItem(item, quotation);
	const isPerGroup = service.serviceType === 'perGroup';
	const salesOrderType = suggestSalesOrderType(service);
	const passengerIds =
		isPerGroup && salesOrderType !== TypeServiceEnum.Transfer && salesOrderType !== TypeServiceEnum.FlightAncillary
			? []
			: savedPassengers.map((p) => p.keyId);
	const parsed = parseTransferFromServiceName(service.name);
	const providerId = service.provider || tariffProviderNotAssignedId;

	return {
		enabled: true,
		salesOrderType,
		otherServiceType: 'otros_servicios',
		flightAncillaryType: suggestFlightAncillaryType(service.name),
		validatingCarrierCode: getValidatingCarrierCodeFromQuotation(quotation),
		feeServiceType: 'fee_por_servicio',
		reference: '',
		tariffProviderId: providerId,
		sameTariffServiceProvider: true,
		serviceProviderId: providerId,
		cost: computeQuotationServiceTotalCost(service, savedPassengers.length),
		...dates,
		passengerIds,
		segment: {
			...parsed,
			startDate: dates.startDate,
			endDate: dates.endDate,
		},
	};
};

export const serviceTypeUsesOtherSubtype = (type: TypeServiceEnum): boolean => type === TypeServiceEnum.Other;

export const serviceTypeShowsTravelDates = (type: TypeServiceEnum): boolean =>
	type === TypeServiceEnum.Other || type === TypeServiceEnum.Transfer;

export const serviceTypeShowsLimitDateOnly = (type: TypeServiceEnum): boolean =>
	type === TypeServiceEnum.FlightAncillary;
